import React from "react";
import "./DataAggregation.scss";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ToolbarSection } from "src/app/generator-data/components/ToolbarSection/ToolbarSection";
import { ReactComponent as DataAggregationIcon } from "src/images/data-aggregation.svg";
import { CustomSwitch } from "src/app/components/CustomSwitch/CustomSwitch";
import { useDispatch, useSelector } from "react-redux";
import {
  setDataAggregationValue,
  setReportGridElements,
  setReportHiddenGridElements,
  setReportLayout,
} from "src/redux";
import { sortLayout } from "src/app/generator-data/functions/sortLayout";
import { formatOptions } from "src/app/ReportGenerator/utils/sizingData";
import { addElementsToLayout } from "src/app/generator-data/functions/addElementsToLayout";
import { showToast } from "src/app/methods/showToast";
import Tooltip from "src/app/components/Tooltip/Tooltip";
import { getDataAggregationSections } from "./utils";

export function DataAggregation() {
  const dataAggregation = useSelector(
    (state) => state.reportReducer.dataAggregation,
  );

  const layout = useSelector((state) => state.reportReducer.layout);
  const gridElements = useSelector((state) => state.reportReducer.gridElements);
  const hiddenGridElements = useSelector(
    (state) => state.reportReducer.hiddenGridElements,
  );
  const canDisplaySentiments = useSelector(
    (state) => state.reportReducer.canDisplaySentiments,
  );

  const format = formatOptions[0].value;

  const dispatch = useDispatch();

  const toggleSectionAggregation = async (types, enabled) => {
    // show aggregated campaigns

    const aggregatedCondition = (item, defaultReturnValue) => {
      if (!types.includes(item.gridBoxType) && !types.includes(item.headerType))
        return defaultReturnValue;

      let aggregatedCondition = item.campaignId !== "aggregated";

      if (enabled) {
        aggregatedCondition = item.campaignId === "aggregated";
      }

      return aggregatedCondition;
    };

    const filteredHiddenGridElements = hiddenGridElements.filter((el) => {
      if (el.gridBoxType === "creator-insights" && !el.isInitiallyVisible)
        return true;

      return (
        !types.includes(el.gridBoxType) &&
        !types.includes(el.headerType) &&
        !aggregatedCondition(el, false)
      );
    });

    const hiddenGridElementsToAdd = gridElements.filter((el) => {
      if (el.gridBoxType === "creator-insights" && !el.isInitiallyVisible)
        return true;

      return (
        (types.includes(el.gridBoxType) || types.includes(el.headerType)) &&
        !aggregatedCondition(el, false)
      );
    });

    // hide separate campaigns
    const filteredGridElements = gridElements.filter((el) => {
      if (el.gridBoxType === "creator-insights" && !el.isInitiallyVisible)
        return false;

      return (
        !types.includes(el.gridBoxType) &&
        !types.includes(el.headerType) &&
        aggregatedCondition(el, true)
      );
    });

    const typesWithRequiredValue = ["creator-insights", "audience-data"];

    const gridElementsToAdd = hiddenGridElements.filter((el) => {
      if (
        typesWithRequiredValue.includes(el.gridBoxType) &&
        !el.isInitiallyVisible
      ) {
        return false;
      }

      return (
        (types.includes(el.gridBoxType) || types.includes(el.headerType)) &&
        aggregatedCondition(el, true)
      );
    });

    const sortedLayout = sortLayout(layout);
    const firstHiddenElementIndex = sortedLayout.findIndex(
      (item) => item.i === hiddenGridElementsToAdd[0]?.label,
    );
    const headerIndex = sortedLayout.findIndex((item) => {
      const conditionOne = item.i.includes("header");
      const conditionTwo = types.some((el) => item.i.includes(el));
      return conditionOne && conditionTwo;
    });

    const getFirstElementIndex = () => {
      if (firstHiddenElementIndex !== -1) return firstHiddenElementIndex;
      if (headerIndex !== -1) return headerIndex;
      return sortedLayout.length - 1;
    };

    const firstElementIndex = getFirstElementIndex();

    const newLayout = sortedLayout.filter((item) => {
      const foundElement = hiddenGridElementsToAdd.find(
        (el) => el.label === item.i,
      );
      return !foundElement;
    });

    const firstLayoutPart = sortedLayout.slice(0, firstElementIndex);
    let lastPart = newLayout.slice(firstElementIndex, newLayout?.length - 1);

    dispatch(
      setReportHiddenGridElements([
        ...filteredHiddenGridElements,
        ...hiddenGridElementsToAdd,
      ]),
    );
    dispatch(
      setReportGridElements([...filteredGridElements, ...gridElementsToAdd]),
    );

    const filteredFirstPart = firstLayoutPart.filter((item) => {
      const foundElement = hiddenGridElementsToAdd.find(
        (el) => el.label === item.i,
      );
      return !foundElement;
    });

    const layoutWithNewElements = await addElementsToLayout(
      gridElementsToAdd,
      [...filteredFirstPart],
      format,
      hiddenGridElements,
    );

    const lastElY =
      newLayout[firstElementIndex]?.h + newLayout[firstElementIndex]?.y;
    const lastEl2 = layoutWithNewElements[layoutWithNewElements?.length - 1];
    const lastEl2Y = lastEl2.y + lastEl2.h;

    const difference =
      Math.ceil((lastEl2Y - lastElY) / format.rows) * format.rows;

    lastPart = lastPart.map((item) => {
      if (item.i.includes("separator")) return item;
      return {
        ...item,
        y: item.y + difference,
      };
    });
    dispatch(setReportLayout([...layoutWithNewElements, ...lastPart]));
  };

  const handleChange = (item, checked) => {
    if (!item?.types) {
      showToast("error", "Data error:", "Grid Box Type not provided");
      return;
    }

    const name = item.value;
    dispatch(setDataAggregationValue(name, checked));
    toggleSectionAggregation(item.types, checked);
  };

  return (
    <ToolbarSection
      className="data-aggregation"
      icon={<DataAggregationIcon />}
      name={
        <IDHFormattedMessage
          id="ws_data_aggregation"
          defaultMessage="Data Aggregation"
        />
      }
      tooltip={
        <Tooltip
          center
          content={
            <IDHFormattedMessage
              id="ws_tooltip_data_aggregation_toolbar"
              defaultMessage="Enable or disable data aggregation. Data from selected campaigns will be summed for the chosen sections."
            />
          }
        />
      }
    >
      {getDataAggregationSections(canDisplaySentiments).map((item) => (
        <a href={item.headerId} className="data-aggregation__item">
          {item.label}
          <CustomSwitch
            checked={dataAggregation[item.value]}
            onChange={(e) => handleChange(item, e.target.checked)}
          />
        </a>
      ))}
    </ToolbarSection>
  );
}
